import { solutionRegexes } from "../../utils/solutionQuery";
import type { Ec2RawInstance, Environment, Stack, StackOsInfo } from "../../types";

function tagValue(tags: Ec2RawInstance["Tags"], key: string): string | undefined {
  return tags?.find((t) => t.Key === key)?.Value;
}

/**
 * Merges Linux+Windows EC2 instances sharing a `dh:stack:id` tag into a
 * single logical stack — mirrors the legacy demandHandler.js grouping.
 */
export function buildStacks(instances: Ec2RawInstance[]): Record<string, Stack> {
  const stacks: Record<string, Stack> = {};

  for (const instance of instances) {
    const tags = instance.Tags ?? [];
    const stackId = tagValue(tags, "dh:stack:id");
    if (!stackId) continue;

    const scheduleRelated: [string, string][] = [];
    let schedule: string | undefined;
    let solution: string | undefined;
    let platform: string | undefined;
    let environment: Environment | undefined;
    let os: string | undefined;
    let version: string | undefined;
    let versionOriginal: string | undefined;
    let stackVersion: string | undefined;

    for (const tag of tags) {
      switch (tag.Key) {
        case "Schedule":
          schedule = tag.Value;
          scheduleRelated.push([tag.Key, tag.Value]);
          break;
        case "DIGHUM_UNREAL_RESTART":
        case "DIGHUM_RESERVATION":
          scheduleRelated.push([tag.Key, tag.Value]);
          break;
        case "DIGHUM_SOLUTION":
          solution = tag.Value;
          break;
        case "dh:stack:platform":
          platform = tag.Value;
          break;
        case "DIGHUM_ENVIRONMENT":
          environment = tag.Value as Environment;
          break;
        case "aws:cloudformation:logical-id":
          os = tag.Value.replace("Instance", "").toLowerCase();
          break;
        case "DIGHUM_SOLUTION_COMMIT":
        case "DIGHUM_SOLUTION_VERSION":
          versionOriginal = tag.Value;
          version = tag.Value.includes("refs/heads") ? tag.Value : tag.Value.substring(0, 7);
          break;
        case "dh:stack:version":
          stackVersion = tag.Value;
          break;
        default:
          break;
      }
    }

    if (!stacks[stackId]) {
      stacks[stackId] = {
        id: stackId,
        solution: solution ?? "",
        environment: environment ?? "DEV",
        platform,
        region: instance.SCRIPT_Region,
        availabilityZone: instance.Placement.AvailabilityZone,
        version,
        versionOriginal,
        stackVersion,
        schedule,
        scheduleRelated,
        status: "",
        playerId: "",
        clientIp: "",
        kioskId: "",
      };
    } else {
      const existing = stacks[stackId];
      for (const pair of scheduleRelated) {
        if (!existing.scheduleRelated.some((p) => p[0] === pair[0])) {
          existing.scheduleRelated.push(pair);
        }
      }
      if (solution) existing.solution = solution;
      if (platform) existing.platform = platform;
      if (environment) existing.environment = environment;
      if (version) existing.version = version;
      if (versionOriginal) existing.versionOriginal = versionOriginal;
      if (stackVersion) existing.stackVersion = stackVersion;
      if (schedule) existing.schedule = schedule;
    }

    if (os === "linux" || os === "windows") {
      const osInfo: StackOsInfo = {
        instanceState: instance.State.Name,
        publicIp: instance.PublicIpAddress,
        instanceId: instance.InstanceId,
        lifecycleType: instance.InstanceLifecycle ?? "",
      };
      stacks[stackId][os] = osInfo;
    }
  }

  return stacks;
}

/**
 * Filters cached EC2 instances by a `|`-separated, `*`-wildcard solution
 * query — same prefix-match semantics as the legacy frontend.
 */
export function filterInstancesBySolution(
  instances: Ec2RawInstance[],
  solutionQuery: string,
): Ec2RawInstance[] {
  const regexes = solutionRegexes(solutionQuery);
  if (regexes.length === 0) return [];
  return instances.filter((instance) => {
    const solutionTag = tagValue(instance.Tags, "DIGHUM_SOLUTION");
    return !!solutionTag && regexes.some((re) => re.test(solutionTag));
  });
}

/** Filters cached EC2 instances down to a known set of `dh:stack:id` values. */
export function filterInstancesByStackIds(
  instances: Ec2RawInstance[],
  stackIds: Iterable<string>,
): Ec2RawInstance[] {
  const ids = new Set(stackIds);
  return instances.filter((instance) => {
    const stackId = tagValue(instance.Tags, "dh:stack:id");
    return !!stackId && ids.has(stackId);
  });
}
